<template>
  <v-dialog
    v-model="dialogOpen"
    v-if="dialogOpen"
    persistent
    scrollable
    max-width="700"
  >
    <v-card class="glass-card">
      <!-- Header -->
      <v-card-title class="glass-header pa-4">
        <v-icon color="white" size="28" class="mr-2">mdi-note-text</v-icon>
        <span class="header-text">Napomena za artikl</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Unos napomene -->
        <v-card outlined class="note-input-card mb-4" elevation="0">
          <v-card-text class="pt-3 pb-2">
            <v-textarea
              ref="noteInput"
              v-model="note"
              label="Unesi napomenu za artikl"
              placeholder="npr. Bez luka, Extra ljuto..."
              rows="2"
              auto-grow
              outlined
              dense
              hide-details="auto"
              class="note-textarea">
            </v-textarea>
          </v-card-text>
        </v-card>

        <!-- Povijest napomena (collapsible) -->
        <v-expansion-panels v-model="historyPanel" v-if="previousNotes.length > 0">
          <v-expansion-panel>
            <v-expansion-panel-header class="expansion-header">
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-history</v-icon>
                <span class="font-weight-bold">Povijest napomena</span>
                <v-chip x-small class="ml-2">{{ previousNotes.length }}</v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-data-table
                :items="previousNotes"
                :headers="headers"
                hide-default-footer
                :items-per-page="-1"
                dense
                class="history-table">
                <!-- Custom cell for timestamp -->
                <template #[`item.note.timestamp`]="{ item }">
                  {{ item.note.formattedTime }}
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <!-- Footer -->
      <v-divider class="footer-divider"></v-divider>
      <v-card-actions class="pa-4 footer-actions">
        <v-spacer></v-spacer>
        <v-btn
          class="save-footer-btn"
          elevation="0"
          large
          @click="close"
          :disabled="!note || note.trim() === ''">
          <v-icon left small>mdi-content-save</v-icon>
          Spremi
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { df } from '@/plugins/firebase'
import { clone } from '@/plugins/utils'
import state from '@/state'

export default {
  data () {
    return {
      previousNotes: [],
      orgUnits: [],
      dialogOpen: false,
      historyPanel: null, // null = collapsed
      note: '',
      itemId: '',
      currentItem: null, // Store current item for discount dialog
      invoice: {},
      headers: []
    }
  },
  methods: {
    open (item, invoice, table) {
      this.note = ''

      this.getHeaders()
      this.getOrganizationalUnits()
      this.dialogOpen = true
      this.itemId = item.id
      this.currentItem = item // Store item reference
      this.invoice = invoice
      this.getPreviousNotes(item, table.id)

      // Auto-focus on note input
      this.$nextTick(() => {
        this.$refs.noteInput?.focus()
      })
    },
    close () {
      this.saveNote()
      this.dialogOpen = false
    },

    openDiscountDialog () {
      // Emit event to parent to open discount dialog with item ID
      this.$emit('open-discount-dialog', this.itemId)
      this.dialogOpen = false
    },

    getHeaders () {
      this.headers = []
      this.headers.push(
        {
          text: 'Napomena',
          align: 'start',
          value: 'note.message',
          width: '40%'
        },
        {
          text: 'Dodao',
          align: 'start',
          value: 'note.addedBy'
        },
        {
          text: 'Vrijeme',
          align: 'start',
          value: 'note.timestamp'
        }
      )
    },
    async getPreviousNotes (item, tableId) {
      this.previousNotes = []
      if (tableId) {
        const tbl = await df.doc(`location_orders/${state.getPointOfSale().id}/tables/${tableId}`).get()
        if (tbl && tbl.data()) {
          const table = tbl.data()
          const items = clone(table.items)

          // Filter only notes for the current item
          items.forEach(i => {
            if (i.id === item.id && i.notes && i.notes.length > 0) {
              i.notes.forEach(note => {
                // Format timestamp if exists
                if (note.timestamp) {
                  const date = new Date(note.timestamp)
                  note.formattedTime = date.toLocaleString('hr-HR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                } else {
                  note.formattedTime = '-'
                }

                this.previousNotes.push({ note: note, item: i })
              })
            }
          })

          // Sort by timestamp descending (newest first)
          this.previousNotes.sort((a, b) => {
            const timeA = a.note.timestamp || 0
            const timeB = b.note.timestamp || 0
            return timeB - timeA
          })
        }
      }
    },
    async getOrganizationalUnits () {
      this.orgUnits = []
      await df.collection(`location_units/${state.getPointOfSale().id}/units`).get()
        .then((documentSnapshots) => {
          if (documentSnapshots.docs && !documentSnapshots.docs.empty) {
            documentSnapshots.docs.forEach((unit) => {
              this.orgUnits.push(unit.data())
            })
          }
        })
    },
    async saveNote () {
      if (this.note.length > 0) {
        const noteItemIdx = this.invoice.items.findIndex(item => item.id === this.itemId)
        if (noteItemIdx > -1) {
          if (!this.invoice.items[noteItemIdx].notes) {
            this.invoice.items[noteItemIdx].notes = []
          }

          const currentUser = state.getUser()
          const message = this.note.replace('\n', '')
          const newNote = {
            orderId: '',
            message,
            currentNote: true,
            addedBy: currentUser ? currentUser.displayName || currentUser.email || 'Nepoznato' : 'Nepoznato',
            timestamp: Date.now()
          }

          this.invoice.items[noteItemIdx].notes.push(newNote)
          // Set note string directly so all save paths (including cloned data) pick it up
          this.invoice.items[noteItemIdx].note = message
        }
      }
    },
    getOrgUnitById (id) {
      return this.orgUnits.find(unit => unit.id === id)
    }
  }
}
</script>

<style scoped>
/* Glass Card Style */
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  box-shadow: 0 8px 32px rgba(57, 137, 201, 0.15) !important;
  overflow: hidden;
}

/* Header with Brand Gradient */
.glass-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  position: relative;
  border-bottom: 1px solid rgba(57, 137, 201, 0.3);
  border-top-left-radius: 16px !important;
  border-top-right-radius: 16px !important;
}

.glass-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
  z-index: 0;
}

.header-text {
  font-size: 20px !important;
  font-weight: 600 !important;
  color: white !important;
  letter-spacing: 0.3px !important;
  position: relative;
  z-index: 1;
}

.close-btn {
  position: relative;
  z-index: 1;
  transition: all 0.2s ease !important;
}

.close-btn:hover {
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.2) !important;
}

/* Note Input Card */
.note-input-card {
  border: 2px solid rgba(57, 137, 201, 0.2) !important;
  border-radius: 12px !important;
  background: rgba(255, 255, 255, 0.5) !important;
  transition: all 0.2s ease !important;
}

.note-input-card:hover {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

/* Note Textarea */
.note-textarea >>> .v-input__control {
  min-height: 52px !important;
}

.note-textarea >>> textarea {
  font-size: 16px !important;
  line-height: 1.5;
}

.note-textarea.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.3) !important;
  border-radius: 8px !important;
}

.note-textarea.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.5) !important;
}

.note-textarea.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

/* Expansion Panel */
.expansion-header {
  min-height: 56px !important;
  font-size: 1.05rem;
  background: rgba(57, 137, 201, 0.03) !important;
}

.expansion-header:hover {
  background: rgba(57, 137, 201, 0.06) !important;
}

/* History Table */
.history-table >>> .v-data-table__wrapper {
  max-height: 300px;
  overflow-y: auto;
}

.history-table >>> .v-data-table thead th {
  background: rgba(57, 137, 201, 0.08) !important;
  color: rgba(70, 70, 80, 0.9) !important;
  font-weight: 600 !important;
  font-size: 13px !important;
}

.history-table >>> .v-data-table tbody tr:hover {
  background: rgba(57, 137, 201, 0.05) !important;
}

/* Footer */
.footer-divider {
  border-color: rgba(57, 137, 201, 0.2) !important;
}

.footer-actions {
  background: rgba(57, 137, 201, 0.02) !important;
  border-bottom-left-radius: 16px !important;
  border-bottom-right-radius: 16px !important;
}

/* Action Icon Button */
.action-icon-btn {
  transition: all 0.2s ease !important;
}

.action-icon-btn:hover {
  background: rgba(57, 137, 201, 0.1) !important;
  transform: scale(1.05);
}

/* Save Button */
.save-footer-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 0 24px !important;
  height: 44px !important;
  min-width: 120px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.save-footer-btn:hover {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #3989C9 !important;
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  transform: translateY(-1px);
}

.save-footer-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}
</style>
